import { useState } from "react";
import { Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { useDeleteSkill, useSetSkill, useSkills } from "./hooks";

const CATEGORIES = ["hardware", "software", "network", "firewall", "server", "cloud", "email", "backup", "security"];

/**
 * Manage an engineer's skills — the deterministic engineer-suggestion scorer
 * boosts an engineer whose skill matches the ticket's category.
 */
export function SkillsDialog({
  userId,
  userName,
  onClose,
}: {
  userId: string | null;
  userName: string;
  onClose: () => void;
}) {
  const { data: skills } = useSkills(userId);
  const set = useSetSkill(userId ?? "");
  const remove = useDeleteSkill(userId ?? "");
  const [skill, setSkill] = useState("network");
  const [level, setLevel] = useState("3");
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    try {
      await set.mutateAsync({ skill, level: Number(level) });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the skill.");
    }
  }

  return (
    <Dialog open={userId !== null} onClose={onClose} title={`Skills — ${userName}`}>
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          {(skills ?? []).length === 0 && (
            <p className="text-xs text-muted-foreground">No skills yet.</p>
          )}
          {(skills ?? []).map((s) => (
            <div key={s.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
              <span className="capitalize">{s.skill}</span>
              <Badge tone="muted">level {s.level}/5</Badge>
              <ConfirmButton
                aria-label={`Remove ${s.skill}`}
                className="ml-auto"
                title="Remove skill?"
                confirmLabel="Remove"
                message={<>Remove skill <span className="font-medium capitalize text-foreground">{s.skill}</span>?</>}
                onConfirm={async () => {
                  await remove.mutateAsync(s.id);
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </ConfirmButton>
            </div>
          ))}
        </div>

        <div className="flex items-end gap-2 border-t border-border pt-3">
          <div className="flex-1">
            <Select aria-label="Skill" value={skill} onChange={(e) => setSkill(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </div>
          <Input
            aria-label="Level"
            type="number"
            min="1"
            max="5"
            className="w-20"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          />
          <Button type="button" variant="outline" disabled={set.isPending} onClick={() => void add()}>
            Add
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
